import request from '@/utils/request'
import Cookies from 'js-cookie'

/**
 * 公告管理接口
 *
 * 后端接口有几处与项目其它接口不同，改动时注意：
 *   1. 分页参数是 currentPage / pageSize，不是 page / limit
 *   2. 请求与响应字段为下划线命名：link_url / start_time / end_time
 *   3. 状态是 1=启用 2=禁用，不是布尔值
 *   4. 分页接口不支持按 type 筛选，类型筛选只能在前端做
 *
 * 为了让页面保持项目统一的驼峰写法，下划线字段的转换全部收在本文件里。
 */

// 公告类型
export const NOTICE_TYPE = {
  MARQUEE: 1,
  POPUP: 2
}

export const NOTICE_TYPE_OPTIONS = [
  { label: '跑马灯', value: NOTICE_TYPE.MARQUEE },
  { label: '弹窗', value: NOTICE_TYPE.POPUP }
]

// 公告状态
export const NOTICE_STATUS = {
  ENABLED: 1,
  DISABLED: 2
}

/**
 * 响应 -> 前端：下划线转驼峰
 * imgFile 是 OSS 对象键（提交用），imgUrl 是后端生成的完整地址（只读，用于预览）
 */
function normalize (item) {
  if (!item) {
    return null
  }
  return {
    id: item.id,
    type: item.type,
    content: item.content,
    imgFile: item.img_file || '',
    imgUrl: item.img_url || '',
    linkUrl: item.link_url || '',
    startTime: item.start_time || '',
    endTime: item.end_time || '',
    status: item.status,
    sort: item.sort
  }
}

/**
 * 前端 -> 请求：驼峰转下划线
 * 时间为空必须传 null，传空串后端会当成非法时间；
 * img_url 是后端生成的，不能回传
 */
function toPayload (data) {
  return {
    type: data.type,
    content: data.content,
    img_file: data.imgFile || '',
    link_url: data.linkUrl || '',
    start_time: data.startTime || null,
    end_time: data.endTime || null,
    status: data.status,
    sort: data.sort
  }
}

/**
 * 公告分页
 * @param {Object} params - { page, limit, type }，内部转成 currentPage / pageSize
 *        type 大于 0 时按公告类型过滤
 */
export function listNotice (params = {}) {
  const query = {
    currentPage: params.page || 1,
    pageSize: params.limit || 10
  }
  if (params.type) {
    query.type = params.type
  }
  return request({
    url: '/notice/page',
    method: 'get',
    params: query
  }).then(res => {
    const body = res.data
    if (body && body.code === 0 && body.data && Array.isArray(body.data.list)) {
      body.data.list = body.data.list.map(normalize)
    }
    return res
  })
}

/**
 * 公告详情
 */
export function getNotice (id) {
  return request({
    url: `/notice/${id}`,
    method: 'get'
  }).then(res => {
    const body = res.data
    if (body && body.code === 0) {
      body.data = normalize(body.data)
    }
    return res
  })
}

/**
 * 新增公告（sort 必须 1~999999）
 */
export function addNotice (data) {
  return request({
    url: '/notice/',
    method: 'post',
    data: toPayload(data)
  })
}

/**
 * 修改公告（sort 允许 0~999999）
 */
export function updateNotice (data) {
  return request({
    url: `/notice/${data.id}`,
    method: 'put',
    data: toPayload(data)
  })
}

/**
 * 修改状态
 */
export function updateNoticeStatus (id, status) {
  return request({
    url: `/notice/${id}/status`,
    method: 'put',
    data: { status }
  })
}

/**
 * 修改排序
 */
export function updateNoticeSort (id, sort) {
  return request({
    url: `/notice/${id}/sort`,
    method: 'put',
    data: { sort }
  })
}

/**
 * 删除公告
 */
export function deleteNotice (id) {
  return request({
    url: `/notice/${id}`,
    method: 'delete'
  })
}

// ---------- 图片上传 ----------

/**
 * OSS 上传地址，供 el-upload 的 action 使用
 */
export const OSS_UPLOAD_URL = `${process.env.VUE_APP_API_TARGET}${process.env.VUE_APP_BASE_API}/system/oss/upload`

/**
 * 上传请求头
 */
export function ossUploadHeaders () {
  return { token: Cookies.get('token') }
}

/**
 * 上传接口返回的是 code=200（不是项目统一的 0），单独判断
 */
export const OSS_UPLOAD_SUCCESS_CODE = 200
